import { useEffect } from "react";
import { SelectProps } from "antd";
import { DefaultSelect } from "ui";

import {
  CURRENCY_OPERATION_CODES,
  CurrencyOperationCodeItem,
  getCurrencyCodesForUved,
} from "./currencyOperationCodes.constants";

const MAX_OPERATION_CODES = 3;

export type CurrencyOperationCodesFieldProps = {
  value?: string[];
  onChange?: (value: string[]) => void;
  /** УВЭД — только значения из справочника; оператор — может добавить произвольный код (tags). */
  allowCustomCodes: boolean;
  contractDirection?: "EXP" | "IMP";
  /** Когда появится в API — подставить из заявки. */
  isReturnDeal?: boolean;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
};

const toOptions = (items: CurrencyOperationCodeItem[]): NonNullable<SelectProps["options"]> =>
  items.map((item) => ({
    value: item.code,
    label: `${item.code} — ${item.title}`,
  }));

export const CurrencyOperationCodesField = ({
  value,
  onChange,
  allowCustomCodes,
  contractDirection,
  isReturnDeal = false,
  disabled,
  placeholder = "Код валютной операции",
  id,
}: CurrencyOperationCodesFieldProps) => {
  const catalogForRole: CurrencyOperationCodeItem[] = allowCustomCodes
    ? CURRENCY_OPERATION_CODES
    : getCurrencyCodesForUved(contractDirection ?? "EXP", isReturnDeal);

  const allowedCodes = new Set(catalogForRole.map((item) => item.code));
  const options = toOptions(
    allowCustomCodes
      ? catalogForRole
      : (() => {
          const selectedUnavailableCodes = CURRENCY_OPERATION_CODES.filter(
            (item) => value?.includes(item.code) && !allowedCodes.has(item.code)
          );

          return [...catalogForRole, ...selectedUnavailableCodes];
        })()
  );

  const mode = allowCustomCodes ? "tags" : "multiple";

  useEffect(() => {
    if (value != null && value.length > MAX_OPERATION_CODES) {
      onChange?.(value.slice(0, MAX_OPERATION_CODES));
    }
  }, [value, onChange]);

  return (
    <DefaultSelect
      id={id}
      mode={mode}
      disabled={disabled}
      value={value?.slice(0, MAX_OPERATION_CODES)}
      onChange={(v) => {
        const nextRaw = Array.isArray(v) ? (v as string[]) : [];
        const currentValue = value ?? [];
        const next = allowCustomCodes
          ? nextRaw
          : nextRaw.filter((code) => allowedCodes.has(code) || currentValue.includes(code));
        onChange?.(next as string[]);
      }}
      options={options}
      placeholder={placeholder}
      allowClear
      showSearch
      size="small"
      className="full-width"
      optionFilterProp="label"
      filterOption={(input, option) => {
        const label = String(option?.label ?? "");
        const optValue = String(option?.value ?? "");
        const q = input.trim().toLowerCase();
        return label.toLowerCase().includes(q) || optValue.toLowerCase().includes(q);
      }}
    />
  );
};
